import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from '@/components/Spinner';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Clock, RefreshCw } from 'lucide-react';

const formSchema = z.object({
  password: z.string().min(8, "La contraseña debe tener al menos 8 caracteres"),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"]
});

const UpdatePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCheckingTokens, setIsCheckingTokens] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasValidTokens, setHasValidTokens] = useState(false);
  const [errorType, setErrorType] = useState<'expired' | 'used' | 'invalid' | 'general'>('general');
  const navigate = useNavigate();
  
  // Store tokens in refs so they persist after URL cleanup
  const accessTokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const checkRecoveryTokens = () => {
      try {
        setIsCheckingTokens(true);
        setError(null);
        
        console.log('[UpdatePassword] Checking recovery tokens...');
        
        // Parse hash parameters
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        // Check for errors first
        const errorParam = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');
        
        if (errorParam) {
          console.error('[UpdatePassword] Error in URL:', { errorParam, errorCode, errorDescription });
          
          // Handle specific error types
          if (errorCode === 'otp_expired' || errorDescription?.includes('expired')) {
            setErrorType('expired');
            setError("El enlace de recuperación ha expirado. Los enlaces solo son válidos durante 1 hora.");
          } else if (errorParam === 'access_denied') {
            setErrorType('used');
            setError("Este enlace ya fue utilizado o es inválido. Cada enlace solo puede usarse una vez.");
          } else {
            setErrorType('invalid');
            setError(`Error en el enlace: ${errorDescription || errorParam}`);
          }
          
          setHasValidTokens(false);
          return;
        }
        
        // Check for valid recovery tokens
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        if (accessToken && refreshToken && type === 'recovery') {
          console.log("[UpdatePassword] Found valid recovery tokens");
          
          // Store tokens in refs before cleaning URL
          accessTokenRef.current = accessToken;
          refreshTokenRef.current = refreshToken;
          
          setHasValidTokens(true);
          
          // Clean the URL for better UX
          window.history.replaceState(null, '', '/update-password');
        } else {
          console.log("[UpdatePassword] No valid recovery tokens found in URL");
          setErrorType('invalid');
          setError("Enlace de recuperación inválido. Por favor, solicita un nuevo enlace.");
          setHasValidTokens(false);
        }
        
      } catch (error: any) {
        console.error("[UpdatePassword] Unexpected error:", error);
        setErrorType('general');
        setError(`Error inesperado: ${error.message}`);
        setHasValidTokens(false);
      } finally {
        setIsCheckingTokens(false);
      }
    };
    
    checkRecoveryTokens();
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!hasValidTokens) {
      setError("No hay tokens válidos. Por favor, solicita un nuevo enlace de recuperación.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[UpdatePassword] Updating password...');
      
      // Get tokens from refs
      const accessToken = accessTokenRef.current;
      const refreshToken = refreshTokenRef.current;
      
      if (!accessToken || !refreshToken) {
        setError("Los tokens de recuperación ya no están disponibles. Por favor, solicita un nuevo enlace.");
        return;
      }
      
      // Clear any existing session before attempting the update
      await supabase.auth.signOut({ scope: 'global' });
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Set session temporarily with recovery tokens
      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      
      if (sessionError) {
        console.error("[UpdatePassword] Session error:", sessionError);
        
        if (sessionError.message.includes('session_not_found') || 
            sessionError.message.includes('AuthSessionMissingError') ||
            sessionError.message.includes('expired')) {
          setErrorType('expired');
          setError("El enlace de recuperación ha expirado. Por favor, solicita un nuevo enlace.");
        } else {
          setError(`Error: ${sessionError.message}`);
        }
        return;
      }
      
      if (!sessionData.session) {
        setError("No se pudo establecer la sesión temporal. Por favor, solicita un nuevo enlace.");
        return;
      }
      
      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.password
      });

      if (updateError) {
        console.error("[UpdatePassword] Error updating password:", updateError);
        
        if (updateError.message.includes('session_not_found') || 
            updateError.message.includes('AuthSessionMissingError')) {
          setError("La sesión ha expirado. Por favor, solicita un nuevo enlace de recuperación.");
        } else {
          setError(`Error al actualizar la contraseña: ${updateError.message}`);
        }
        return;
      }

      console.log('[UpdatePassword] Password updated successfully');
      setIsSubmitted(true);
      
      toast({
        title: "Éxito",
        description: "Contraseña actualizada con éxito",
        variant: "default"
      });
      
      // Clear the stored tokens
      accessTokenRef.current = null;
      refreshTokenRef.current = null;
      
      // Sign out and redirect after successful password change
      setTimeout(async () => {
        await supabase.auth.signOut({ scope: 'global' });
        navigate('/login');
      }, 2000);
      
    } catch (error: any) {
      console.error("[UpdatePassword] Unexpected error:", error);
      setError(`Error inesperado: ${error.message || 'Error desconocido'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorIcon = () => {
    switch (errorType) {
      case 'expired':
        return <Clock className="h-4 w-4" />;
      case 'used':
        return <RefreshCw className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getErrorVariant = () => {
    return errorType === 'expired' || errorType === 'used' ? 'default' : 'destructive';
  };

  if (isCheckingTokens) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Verificando enlace de recuperación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Actualizar Contraseña</CardTitle>
          <CardDescription className="text-center">
            Establece una nueva contraseña para tu cuenta
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant={getErrorVariant()} className="mb-4">
              {getErrorIcon()}
              <AlertTitle>
                {errorType === 'expired' ? 'Enlace Expirado' : 
                 errorType === 'used' ? 'Enlace Ya Utilizado' : 'Error'}
              </AlertTitle>
              <AlertDescription className="mt-2">
                {error}
                <div className="mt-3 space-y-2">
                  <Button 
                    onClick={() => navigate('/password-reset')} 
                    className="w-full bg-vegana-primary hover:bg-vegana-accent"
                    size="sm"
                  >
                    Solicitar nuevo enlace
                  </Button>
                  {errorType === 'expired' && (
                    <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
                      <strong>Consejo:</strong> Los enlaces expiran en 1 hora. Para evitar este problema, 
                      usa el enlace inmediatamente después de recibirlo.
                    </div>
                  )}
                  {errorType === 'used' && (
                    <div className="text-xs text-muted-foreground bg-orange-50 p-2 rounded">
                      <strong>Consejo:</strong> Cada enlace solo puede usarse una vez. Si ya cambiaste 
                      tu contraseña, intenta iniciar sesión con la nueva contraseña.
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {isSubmitted ? (
            <div className="text-center space-y-4">
              <p className="text-green-600">Contraseña actualizada con éxito.</p>
              <p>Serás redirigido al inicio de sesión...</p>
              <div className="flex justify-center mt-4">
                <Spinner size="md" />
              </div>
            </div>
          ) : hasValidTokens ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nueva Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="********" {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmar Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="********" {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-vegana-primary hover:bg-vegana-accent" 
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" /> : "Actualizar Contraseña"}
                </Button>
              </form>
            </Form>
          ) : null}
        </CardContent>
        <CardFooter className="flex justify-center">
          <div className="text-sm text-center">
            <Link to="/login" className="text-vegana-primary hover:text-vegana-accent">
              Volver al inicio de sesión
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UpdatePassword;
